import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import api from "../api/axios";
import { useAuth } from "../context/AuthContext";
import FinancePageHeader from "../components/finance/FinancePageHeader";
import DashboardLayout from "../components/Layout/DashboardLayout";

const initialForm = {
  amount: "",
  currency: "USD",
  source: "",
  funding_date: new Date().toISOString().slice(0, 10),
  note: "",
};

const formatMoney = (value, currency) => {
  const amount = Number(value || 0);
  return `${amount.toLocaleString()} ${currency || ""}`.trim();
};

const CashFundingsPage = () => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [form, setForm] = useState(initialForm);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const assignedAgencyLabel = useMemo(() => {
    if (!user?.agency_id) return "";
    return [
      user?.agency_name || "",
      user?.city || user?.location_city || "",
      user?.country || user?.location_country || "",
    ]
      .filter(Boolean)
      .join(" • ");
  }, [user]);

  const fetchFundings = useCallback(async () => {
    if (!user?.agency_id) return;

    try {
      setLoading(true);
      const res = await api.get("/cash-fundings", {
        params: { agency_id: user.agency_id },
      });

      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      enqueueSnackbar("Échec du chargement des approvisionnements", {
        variant: "error",
      });
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [user?.agency_id, enqueueSnackbar]);

  useEffect(() => {
    fetchFundings();
  }, [fetchFundings]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!user?.agency_id) {
      enqueueSnackbar("Votre compte n’a pas d’agence assignée", {
        variant: "error",
      });
      return;
    }

    const numericAmount = Number(form.amount);
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      enqueueSnackbar("Montant invalide", { variant: "warning" });
      return;
    }

    try {
      setSubmitting(true);

      await api.post("/cash-fundings", {
        agency_id: Number(user.agency_id),
        amount: numericAmount,
        currency: form.currency,
        source: form.source.trim() || null,
        funding_date: form.funding_date || null,
        note: form.note.trim() || null,
      });

      enqueueSnackbar("Approvisionnement enregistré avec succès", {
        variant: "success",
      });

      setForm(initialForm);
      fetchFundings();
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        "Échec de l'enregistrement de l’approvisionnement";
      enqueueSnackbar(msg, { variant: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout
      role={user?.role}
      activePage="/cash-fundings"
      title="Approvisionnements"
    >
      <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
        <Stack spacing={2.5}>
          <FinancePageHeader
            title="Approvisionnements de caisse"
            subtitle="Ajoutez les entrées de caisse et consultez l’historique des approvisionnements de votre agence."
          />

          <Alert severity="info" sx={{ borderRadius: 3 }}>
            <strong>Agence :</strong>{" "}
            {assignedAgencyLabel || `Agence #${user?.agency_id || "non définie"}`}
          </Alert>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            useFlexGap
            flexWrap="wrap"
          >
            <Button variant="outlined" onClick={() => navigate("/cashboxes")}>
              Retour aux caisses
            </Button>
            <Button variant="text" onClick={fetchFundings}>
              Actualiser
            </Button>
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={12} lg={5}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
                  Nouvel approvisionnement
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Montant"
                      name="amount"
                      type="number"
                      value={form.amount}
                      onChange={handleChange}
                      inputProps={{ min: 0, step: "0.01" }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      fullWidth
                      label="Devise"
                      name="currency"
                      value={form.currency}
                      onChange={handleChange}
                    >
                      <MenuItem value="USD">USD</MenuItem>
                      <MenuItem value="CDF">CDF</MenuItem>
                      <MenuItem value="RMB">RMB</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Source"
                      name="source"
                      value={form.source}
                      onChange={handleChange}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Date"
                      name="funding_date"
                      type="date"
                      value={form.funding_date}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      label="Note"
                      name="note"
                      value={form.note}
                      onChange={handleChange}
                    />
                  </Grid>
                </Grid>

                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2.5 }}>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={submitting}
                  >
                    {submitting ? "Enregistrement..." : "Enregistrer"}
                  </Button>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} lg={7}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
                  Historique des approvisionnements
                </Typography>

                {loading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : items.length === 0 ? (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    Aucun approvisionnement enregistré.
                  </Alert>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Montant</TableCell>
                          <TableCell>Devise</TableCell>
                          <TableCell>Source</TableCell>
                          <TableCell>Créé par</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {items.map((row) => (
                          <TableRow key={row.id} hover>
                            <TableCell>{row.funding_date || "-"}</TableCell>
                            <TableCell>{formatMoney(row.amount, row.currency)}</TableCell>
                            <TableCell>{row.currency || "-"}</TableCell>
                            <TableCell>{row.source || "-"}</TableCell>
                            <TableCell>{row.created_by_name || "-"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Stack>
      </Container>
    </DashboardLayout>
  );
};

export default CashFundingsPage;